package com.anjuros.website.redesign.sections.pricing

import com.anjuros.website.redesign.util.RdRoute
import com.anjuros.website.redesign.util.RdText

/** A one-off website option. [fromPrice] marks a starting price ("from £149"); [link] is an optional link under the features. */
enum class WebsiteOption(
    val badge: RdText,
    val title: RdText,
    val price: RdText,
    val text: RdText,
    val features: RdText,
    val link: RdText?,
    val linkPath: String?,
    val fromPrice: Boolean,
    val offerPrice: String,
) {
    CUSTOM(
        RdText.WEBSITE_CUSTOM_BADGE, RdText.WEBSITE_CUSTOM_TITLE, RdText.WEBSITE_CUSTOM_PRICE, RdText.WEBSITE_CUSTOM_TEXT,
        RdText.WEBSITE_CUSTOM_FEATURES, RdText.WEBSITE_CUSTOM_LINK, RdRoute.DESIGNS, fromPrice = false, offerPrice = "99.00",
    ),
    BESPOKE(
        RdText.WEBSITE_BESPOKE_BADGE, RdText.WEBSITE_BESPOKE_TITLE, RdText.WEBSITE_BESPOKE_PRICE, RdText.WEBSITE_BESPOKE_TEXT,
        RdText.WEBSITE_BESPOKE_FEATURES, link = null, linkPath = null, fromPrice = true, offerPrice = "149.00",
    ),
}

/** A monthly care plan. [includesPrevious] is the "Everything in ..." line, [badge] highlights a plan. */
enum class CarePlan(
    val title: RdText,
    val price: RdText,
    val text: RdText,
    val features: RdText,
    val includesPrevious: RdText? = null,
    val badge: RdText? = null,
    val offerPrice: String,
) {
    BASIC(RdText.PLAN_BASIC_TITLE, RdText.PLAN_BASIC_PRICE, RdText.PLAN_BASIC_TEXT, RdText.PLAN_BASIC_FEATURES, offerPrice = "9.99"),
    PLUS(
        RdText.PLAN_PLUS_TITLE, RdText.PLAN_PLUS_PRICE, RdText.PLAN_PLUS_TEXT, RdText.PLAN_PLUS_FEATURES,
        includesPrevious = RdText.PLAN_EVERYTHING_IN_BASIC, offerPrice = "19.99",
    ),
    FULL(
        RdText.PLAN_FULL_TITLE, RdText.PLAN_FULL_PRICE, RdText.PLAN_FULL_TEXT, RdText.PLAN_FULL_FEATURES,
        includesPrevious = RdText.PLAN_EVERYTHING_IN_PLUS, badge = RdText.PLAN_FULL_BADGE, offerPrice = "39.99",
    ),
}

/** One row of the comparison table: what each plan gets, from Basic to Full Service. */
class CompareRow(val feature: RdText, val basic: CompareCell, val plus: CompareCell, val full: CompareCell)

sealed class CompareCell {
    data object Yes : CompareCell()
    data object No : CompareCell()
    class Value(val text: RdText) : CompareCell()
    /** "Paid per update" with the Basic plan's update price list. */
    data object BasicUpdates : CompareCell()
}

class CompareGroup(val title: RdText, val rows: List<CompareRow>)

private fun allPlans(feature: RdText) = CompareRow(feature, CompareCell.Yes, CompareCell.Yes, CompareCell.Yes)
private fun plusAndFull(feature: RdText) = CompareRow(feature, CompareCell.No, CompareCell.Yes, CompareCell.Yes)
private fun fullOnly(feature: RdText) = CompareRow(feature, CompareCell.No, CompareCell.No, CompareCell.Yes)

val CompareGroups = listOf(
    CompareGroup(
        RdText.COMPARE_GROUP_ONLINE, listOf(
            allPlans(RdText.COMPARE_DOMAIN),
            allPlans(RdText.COMPARE_HOSTING),
            allPlans(RdText.COMPARE_SSL),
            allPlans(RdText.COMPARE_BACKUPS),
            allPlans(RdText.COMPARE_SECURITY),
            allPlans(RdText.COMPARE_ENQUIRIES),
        )
    ),
    CompareGroup(
        RdText.COMPARE_GROUP_GROWING, listOf(
            CompareRow(
                RdText.COMPARE_UPDATES, CompareCell.BasicUpdates,
                CompareCell.Value(RdText.COMPARE_UPDATES_PLUS), CompareCell.Value(RdText.COMPARE_UPDATES_FULL)
            ),
            plusAndFull(RdText.COMPARE_SEO),
            plusAndFull(RdText.COMPARE_HEALTH),
            plusAndFull(RdText.COMPARE_SPEED),
        )
    ),
    CompareGroup(
        RdText.COMPARE_GROUP_BUSINESS, listOf(
            fullOnly(RdText.COMPARE_PORTAL),
            fullOnly(RdText.COMPARE_JOBS),
            fullOnly(RdText.COMPARE_RECORDS),
            fullOnly(RdText.COMPARE_REQUESTS),
            fullOnly(RdText.COMPARE_SUPPORT),
        )
    ),
)
